import { Card, CardContent } from "@/components/ui/card";
import { ThemeToggle } from "@/components/theme-toggle";
import { AlertTriangle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import type { LegalDocumentStatus } from "@shared/schema";

export default function TermsOfServicePage() {
  const { data: statuses = [] } = useQuery<LegalDocumentStatus[]>({
    queryKey: ["/api/public/legal-status"],
  });
  const status = statuses.find((s) => s.documentSlug === "terms");
  const showDraft = status?.isDraft ?? true;
  const note = status?.bannerNote;
  const reviewedAt = status?.lastReviewedAt ? new Date(status.lastReviewedAt) : null;
  const reviewedBy = status?.lastReviewedBy ?? null;
  const reviewedDateLabel = reviewedAt
    ? reviewedAt.toISOString().slice(0, 10)
    : null;
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto flex h-14 items-center justify-between px-4">
          <a href="/" className="font-semibold text-lg" data-testid="link-home">
            Clarik
          </a>
          <ThemeToggle />
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-3xl">
        {showDraft ? (
          <div
            className="mb-6 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-900/60 dark:bg-amber-950/30"
            data-testid="banner-terms-draft"
          >
            <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-600 dark:text-amber-400" />
            <div className="text-sm">
              <div className="font-semibold text-amber-900 dark:text-amber-200">
                DRAFT — pending review by legal counsel
              </div>
              <div className="text-amber-900/90 dark:text-amber-200/90">
                {note ??
                  "This Terms of Service is published in good-faith working draft form while it is reviewed by external counsel. Final binding language is forthcoming."}
                <span className="ml-1 italic">An administrator can flip this banner off after counsel review.</span>
              </div>
            </div>
          </div>
        ) : reviewedDateLabel ? (
          <div
            className="mb-6 text-sm text-muted-foreground"
            data-testid="text-terms-reviewed"
          >
            Last reviewed {reviewedDateLabel}
            {reviewedBy ? ` by ${reviewedBy}` : ""}.
          </div>
        ) : note ? (
          <div
            className="mb-6 text-sm text-muted-foreground"
            data-testid="text-terms-reviewed"
          >
            {note}
          </div>
        ) : null}

        <Card>
          <CardContent className="prose dark:prose-invert max-w-none p-6 md:p-8">
            <h1 className="text-2xl md:text-3xl font-bold mb-6" data-testid="text-terms-title">
              Terms of Service – Clarik
            </h1>
            
            <p className="text-muted-foreground mb-8">
              Effective Date: April 25, 2026
              {showDraft && (
                <>
                  {" — "}
                  <span className="font-medium text-amber-700 dark:text-amber-400">DRAFT</span>
                </>
              )}
            </p>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">1. Acceptance of Terms</h2>
              <p className="text-foreground/90 leading-relaxed">
                By accessing or using Clarik, you agree to be bound by these Terms of Service. 
                If you do not agree to these terms, please do not use the service. Your continued use 
                of Clarik constitutes acceptance of any updates or modifications to these terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">2. Description of Service</h2>
              <p className="text-foreground/90 leading-relaxed">
                Clarik is a customer relationship management (CRM) platform designed for 
                entertainment and service-based businesses. The platform helps users manage contacts, 
                leads, and clients, and supports optional email integrations (such as Gmail via OAuth) 
                for sending user-initiated emails and logging related replies. Additional features may 
                include scheduling, invoicing, reporting, and optional social media management
                tools that integrate with third-party platforms (currently Meta&rsquo;s Facebook
                Pages and Instagram Business APIs, TikTok Content Posting, and YouTube Data API).
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">2a. Third-Party Platform Terms</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                When you connect a third-party social media account (Meta, TikTok, or YouTube) to
                Clarik, you also agree to that platform&rsquo;s applicable developer and
                end-user terms, in addition to these Terms of Service. Specifically:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-foreground/90">
                <li>
                  <strong>Meta (Facebook &amp; Instagram):</strong> use of Clarik&rsquo;s
                  Facebook / Instagram features is governed by the{" "}
                  <a href="https://developers.facebook.com/terms/" target="_blank" rel="noreferrer" className="text-primary hover:underline">
                    Meta Platform Terms
                  </a>{" "}
                  and{" "}
                  <a href="https://developers.facebook.com/devpolicy/" target="_blank" rel="noreferrer" className="text-primary hover:underline">
                    Developer Policies
                  </a>.
                </li>
                <li>
                  <strong>TikTok:</strong> use of Clarik&rsquo;s TikTok features is governed
                  by the{" "}
                  <a href="https://developers.tiktok.com/terms-of-service/" target="_blank" rel="noreferrer" className="text-primary hover:underline">
                    TikTok Developer Terms of Service
                  </a>.
                </li>
                <li>
                  <strong>YouTube:</strong> use of Clarik&rsquo;s YouTube features is governed
                  by the{" "}
                  <a href="https://developers.google.com/youtube/terms/api-services-terms-of-service" target="_blank" rel="noreferrer" className="text-primary hover:underline">
                    YouTube API Services Terms of Service
                  </a>{" "}
                  and the{" "}
                  <a href="https://www.youtube.com/t/terms" target="_blank" rel="noreferrer" className="text-primary hover:underline">
                    YouTube Terms of Service
                  </a>. You can revoke Clarik&rsquo;s access at{" "}
                  <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer" className="text-primary hover:underline">
                    https://myaccount.google.com/permissions
                  </a>.
                </li>
              </ul>
              <p className="text-foreground/90 leading-relaxed mt-4">
                You are solely responsible for the content you publish through Clarik to
                any connected platform, including ensuring you have the rights to publish that
                content and that it complies with the destination platform&rsquo;s community
                guidelines.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">3. Account Registration and Security</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                When you create an account with Clarik, you agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-foreground/90">
                <li>Provide accurate and complete registration information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Promptly update your information if it changes</li>
                <li>Accept responsibility for all activity that occurs under your account</li>
                <li>Notify us immediately of any unauthorized access or security breaches</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">4. Permitted Use</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                <strong>You may use Clarik for:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2 text-foreground/90 mb-4">
                <li>Lawful business communications with your contacts</li>
                <li>Managing your own leads, clients, and customer relationships</li>
                <li>Normal CRM functionality including scheduling, invoicing, and reporting</li>
                <li>Sending emails to contacts who have a legitimate business relationship with you</li>
              </ul>
              <p className="text-foreground/90 leading-relaxed mb-4">
                <strong>You may NOT use Clarik for:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2 text-foreground/90">
                <li>Sending spam or unsolicited bulk emails</li>
                <li>Using purchased, rented, or scraped contact lists</li>
                <li>Harassment, threats, or abusive communications</li>
                <li>Deceptive, fraudulent, or misleading content</li>
                <li>Any illegal activities or violations of applicable laws</li>
                <li>Attempting to bypass sending limits, deliverability safeguards, or security measures</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">5. Email Integrations and User Responsibility</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                When using email integrations (such as Gmail), you acknowledge and agree that:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-foreground/90">
                <li>
                  <strong>User-Initiated Sending:</strong> All emails sent through Clarik are 
                  initiated by you, the user. We do not send emails on your behalf without your 
                  explicit action.
                </li>
                <li>
                  <strong>Compliance Responsibility:</strong> You are responsible for ensuring your 
                  email communications comply with all applicable laws, including CAN-SPAM, GDPR, 
                  and other anti-spam regulations, as well as recipient preferences.
                </li>
                <li>
                  <strong>Deliverability Protections:</strong> Clarik may enforce deliverability 
                  protections including throttling, rate limiting, warmup schedules, and automatic 
                  pausing of campaigns to protect domain reputation and email deliverability. These 
                  measures help ensure your emails reach recipients and protect the platform for all users.
                </li>
                <li>
                  <strong>Opt-Out Compliance:</strong> We automatically process and honor opt-out 
                  requests (such as "stop" or "unsubscribe" replies) by adding contacts to suppression 
                  lists to prevent further outreach.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">6. Data and Privacy</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                Your privacy is important to us. Our use of your data is governed by our{" "}
                <a href="/privacy" className="text-primary hover:underline" data-testid="link-privacy">
                  Privacy Policy
                </a>, which is incorporated into these Terms by reference.
              </p>
              <p className="text-foreground/90 leading-relaxed">
                We do not sell your data or the data you store in Clarik. Your information 
                is handled in accordance with our Privacy Policy and applicable data protection laws.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">7. Billing and Subscription</h2>
              <p className="text-foreground/90 leading-relaxed">
                If you subscribe to a paid plan, billing terms will be presented at the time of 
                purchase and are incorporated by reference into these Terms. Subscription fees are 
                billed in advance on a recurring basis. You are responsible for any applicable taxes. 
                Unless you cancel before your renewal date, your subscription will automatically renew 
                and you authorize us to charge the applicable fees.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">8. Intellectual Property</h2>
              <p className="text-foreground/90 leading-relaxed">
                Clarik and its original content, features, and functionality are owned by 
                Clarik and are protected by copyright, trademark, and other intellectual 
                property laws. You retain full ownership of the content and data you upload or 
                create within the platform. By using Clarik, you grant us a limited license 
                to host and display your content solely as necessary to provide the service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">9. Disclaimers</h2>
              <p className="text-foreground/90 leading-relaxed">
                Clarik is provided "as is" and "as available" without warranties of any kind, 
                either express or implied. We do not warrant that the service will be uninterrupted, 
                error-free, or completely secure. Email deliverability is not guaranteed, as it 
                depends on many factors outside our control, including recipient email providers, 
                spam filters, and your sending practices.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">10. Limitation of Liability</h2>
              <p className="text-foreground/90 leading-relaxed">
                To the maximum extent permitted by law, Clarik's total liability for any 
                claims arising from your use of the service is limited to the greater of (a) the 
                amount you paid us in the three (3) months preceding the claim, or (b) one hundred 
                dollars ($100). In no event shall we be liable for any indirect, incidental, special, 
                consequential, or punitive damages, including loss of profits, data, or business 
                opportunities.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">11. Termination</h2>
              <p className="text-foreground/90 leading-relaxed">
                We may suspend or terminate your access to Clarik at any time for violations 
                of these Terms or for any other reason at our discretion. You may stop using the 
                service at any time. Upon termination, your right to use the service ceases immediately, 
                though you may request export of your data in accordance with our Privacy Policy.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">12. Changes to Terms</h2>
              <p className="text-foreground/90 leading-relaxed">
                We may update these Terms from time to time to reflect changes in our service or 
                legal requirements. When we make material changes, we will update the "Effective Date" 
                at the top of this page. Your continued use of Clarik after any changes 
                constitutes acceptance of the updated Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">13. Security Commitments</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                We commit to the following baseline security controls for the Clarik service:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-foreground/90">
                <li>
                  <strong>Encryption at rest:</strong> Customer data is encrypted using
                  envelope encryption with AES-256-GCM data keys wrapped by managed
                  key-encryption keys (KEK). Particularly sensitive contact and payment
                  fields receive an additional layer of field-level encryption.
                </li>
                <li>
                  <strong>Encryption in transit:</strong> All API and browser traffic is
                  served over TLS 1.2+; integrations to email and calendar providers use
                  the providers' OAuth2 + TLS-protected APIs.
                </li>
                <li>
                  <strong>Loose-PII minimization (no-loose-PII rule):</strong> We do not
                  log full payment instruments, government IDs, or audience PII to
                  application logs, error trackers, or analytics. The HTTP access
                  logger does not capture response bodies in production — only
                  request method, path, status code, and duration are retained —
                  so email addresses, phone numbers, and other contact-record
                  fields never reach the long-lived log store in the first place.
                </li>
                <li>
                  <strong>Access control:</strong> Role-based permissions, workspace
                  isolation, and least-privilege defaults govern access to all customer
                  data. Privileged actions are logged.
                </li>
                <li>
                  <strong>Breach notification:</strong> In the event of a confirmed
                  security incident affecting your data, we will notify the workspace
                  owner without undue delay and in no event more than seventy-two (72)
                  hours after confirmation, with the information required by applicable
                  data-protection regulations.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">14. Data Retention</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                We retain customer data for as long as your subscription is active and
                for a limited period thereafter to support recovery and audit needs:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-foreground/90">
                <li>
                  <strong>Live data:</strong> Retained while your subscription is active.
                </li>
                <li>
                  <strong>Soft-deleted records:</strong> Recoverable for seven (7) days,
                  then permanently purged.
                </li>
                <li>
                  <strong>Backups:</strong> Encrypted backups are taken daily
                  (Recovery Point Objective of no more than twenty-four (24)
                  hours) and retained under a weekly + monthly anchor policy:
                  by default every backup from the last twelve (12) weeks is
                  kept, plus one anchor backup from each of the prior twelve
                  (12) months. Older or non-anchor backups are pruned
                  automatically; failed or in-progress runs are preserved
                  while still recent. Restore is tested on a scheduled cadence
                  with results alerted to operators.
                </li>
                <li>
                  <strong>Logs &amp; audit trails:</strong> Retained for at least
                  three hundred sixty-five (365) days in an append-only database
                  protected against in-place edits, deletions, and table
                  truncation. Daily off-database exports are archived to
                  encrypted object storage as an additional tamper-resistance
                  layer. We are working toward a longer multi-year retention
                  target to support financial-services and SOC2 evidence
                  requirements; the current effective floor is three hundred
                  sixty-five (365) days.
                </li>
                <li>
                  <strong>Account closure:</strong> You may request deletion of
                  your account and all associated data at any time via the
                  public deletion request form at <code>/data-deletion</code>
                  or by contacting support. Production data is permanently
                  removed within thirty (30) days of a verified request, and
                  the corresponding rows are removed from backups in the next
                  pruning cycle as the underlying weekly / monthly anchors
                  roll off. Automated deletion triggered solely by account
                  inactivity following cancellation is an aspirational target
                  and is not yet enforced by an automated job; closures today
                  are driven by the request path described above.
                </li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">15. [Reserved]</h2>
              <p className="text-foreground/90 leading-relaxed">
                This section number is reserved. The Contact section previously
                numbered §15 was renumbered to §18 in the 2026-04-25 amendment that
                introduced §16 (Governing Law) and §17 (Dispute Resolution).
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">16. Governing Law</h2>
              <p className="text-foreground/90 leading-relaxed">
                These Terms of Service, and any dispute or claim (including non-contractual
                disputes or claims) arising out of or in connection with them, their subject
                matter, or their formation, are governed by and construed in accordance with
                the laws of the State of Delaware, United States, without regard to its
                conflict-of-laws principles. The United Nations Convention on Contracts for
                the International Sale of Goods does not apply. Subject to §17 (Dispute
                Resolution) below, the parties irrevocably submit to the exclusive jurisdiction
                of the state and federal courts located in New Castle County, Delaware for any
                action not subject to arbitration.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">17. Dispute Resolution</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                <strong>Informal resolution first.</strong> Most disputes can be resolved without
                formal proceedings. Before initiating arbitration or any court action, you agree
                to first send a written notice describing the dispute and your contact
                information to{" "}
                <a
                  href="mailto:legal@clarik.io"
                  className="text-primary hover:underline"
                  data-testid="link-legal-email"
                >
                  legal@clarik.io
                </a>
                . The parties will attempt in good faith to resolve the dispute within thirty
                (30) days of receipt of the notice.
              </p>
              <p className="text-foreground/90 leading-relaxed mb-4">
                <strong>Binding individual arbitration.</strong> If the dispute is not resolved
                informally, you and Clarik agree that any dispute, claim, or controversy
                arising out of or relating to these Terms or your use of Clarik
                (a &ldquo;Dispute&rdquo;) shall be finally resolved by binding individual
                arbitration administered by JAMS under its Comprehensive Arbitration Rules and
                Procedures then in effect. The arbitration shall be conducted in the English
                language and seated in Wilmington, Delaware, unless the parties agree otherwise.
                The arbitrator&rsquo;s award shall be final and binding, and judgment on the
                award may be entered in any court of competent jurisdiction. Each party shall
                bear its own costs and attorneys&rsquo; fees, except that the arbitrator may
                award fees and costs to the prevailing party where authorized by applicable law.
              </p>
              <p className="text-foreground/90 leading-relaxed mb-4">
                <strong>Class-action waiver.</strong> YOU AND CLARIK AGREE THAT EACH MAY
                BRING CLAIMS AGAINST THE OTHER ONLY IN AN INDIVIDUAL CAPACITY, AND NOT AS A
                PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, COLLECTIVE, CONSOLIDATED, OR
                REPRESENTATIVE PROCEEDING. The arbitrator may not consolidate more than one
                party&rsquo;s claims and may not preside over any form of class or
                representative proceeding. If this class-action waiver is found unenforceable as
                to a particular claim, then that claim (and only that claim) shall be severed
                from the arbitration and brought in the courts identified in §16, while all
                other claims shall continue in arbitration.
              </p>
              <p className="text-foreground/90 leading-relaxed mb-4">
                <strong>Carve-outs.</strong> Notwithstanding the foregoing, either party may
                (a) bring an individual action in small-claims court for any Dispute within that
                court&rsquo;s jurisdictional limits; (b) seek injunctive or other equitable
                relief in a court of competent jurisdiction to prevent or restrain actual or
                threatened infringement, misappropriation, or violation of its intellectual
                property, trade secrets, or confidential information; and (c) seek to enforce
                any arbitration award in any court of competent jurisdiction.
              </p>
              <p className="text-foreground/90 leading-relaxed">
                <strong>Opt-out right.</strong> You may opt out of this §17 (other than the
                informal-resolution requirement) by sending a written opt-out notice to{" "}
                <a
                  href="mailto:legal@clarik.io"
                  className="text-primary hover:underline"
                  data-testid="link-legal-email-optout"
                >
                  legal@clarik.io
                </a>{" "}
                within thirty (30) days of first accepting these Terms. Your notice must include
                your full name, the email address associated with your account, and a clear
                statement that you wish to opt out of arbitration. Opting out will not
                otherwise affect these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold mb-4">18. Contact</h2>
              <p className="text-foreground/90 leading-relaxed mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-muted/50 border rounded-lg p-4">
                <p className="text-foreground/90">
                  <strong>Email:</strong>{" "}
                  <a 
                    href="mailto:admin@entertaincrm.com" 
                    className="text-primary hover:underline"
                    data-testid="link-support-email"
                  >
                    admin@entertaincrm.com
                  </a>
                </p>
              </div>
            </section>

            <hr className="my-8 border-border" />

            <p className="text-muted-foreground text-sm">
              By using Clarik, you acknowledge that you have read, understood, and agree 
              to be bound by these Terms of Service.
            </p>
          </CardContent>
        </Card>
      </main>

      <footer className="border-t py-6 mt-8">
        <div className="container mx-auto px-4 text-center text-muted-foreground text-sm">
          <div className="flex justify-center gap-4 mb-2">
            <a href="/privacy" className="hover:underline" data-testid="link-footer-privacy">Privacy Policy</a>
            <span>|</span>
            <a href="/terms" className="hover:underline" data-testid="link-footer-terms">Terms of Service</a>
          </div>
          <p>&copy; {new Date().getFullYear()} Clarik. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
